import { useState, useEffect } from "react"
import { usePreviewConnectorMutation } from "../../../../../../redux/queries"

interface UseTemplatePreviewProps {
  selectedRecordId: string
  dataType: string
  template?: string
  fieldMapping: any
  connectorId?: string
  isPanelOpen: boolean
}

export const useTemplatePreview = ({
  selectedRecordId,
  dataType,
  template,
  fieldMapping,
  connectorId,
  isPanelOpen
}: UseTemplatePreviewProps) => {
  const [mappedPreview, setMappedPreview] = useState<any>(null)
  const [showPreview, setShowPreview] = useState(false)

  const [previewConnector, { isLoading: isFetchingRecord }] =
    usePreviewConnectorMutation()

  const generatePreview = async () => {
    if (!selectedRecordId || !connectorId) {
      setMappedPreview(null)
      setShowPreview(false)
      return
    }

    // lead_score and pending_subscription connectors send a fixed schema — no
    // template/mapping to preview. The static schema preview lives in the
    // Configuration section instead.
    if (dataType === "lead_score" || dataType === "pending_subscription") {
      setMappedPreview(null)
      setShowPreview(false)
      return
    }

    try {
      // Call the backend preview endpoint
      const params: any = {
        connector_id: connectorId
      }

      // Add the appropriate record ID based on data type
      if (dataType === "lead") {
        params.lead_id = selectedRecordId
      } else if (dataType === "subscription") {
        params.subscription_id = selectedRecordId
      } else if (dataType === "pending_subscription") {
        params.pending_subscription_id = selectedRecordId
      }

      const result = await previewConnector(params).unwrap()

      if (result.status === "success") {
        // Handle null or empty payload
        if (!result.payload || Object.keys(result.payload).length === 0) {
          setMappedPreview({
            note: "No template configured or template processing returned empty result",
            webhook_url: result.webhook_url,
            headers: result.headers
          })
        } else {
          setMappedPreview(result.payload)
        }
        setShowPreview(true)
      } else {
        setMappedPreview({
          error: "Failed to generate preview",
          message: result.message
        })
        setShowPreview(true)
      }
    } catch (error) {
      console.error("Preview generation error:", error)
      setMappedPreview({
        error: "Failed to generate preview",
        details: error instanceof Error ? error.message : String(error)
      })
      setShowPreview(true)
    }
  }

  useEffect(() => {
    if (selectedRecordId && isPanelOpen) {
      generatePreview()
    } else {
      setShowPreview(false)
      setMappedPreview(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecordId, isPanelOpen])

  // Clear preview when panel closes
  useEffect(() => {
    if (!isPanelOpen) {
      setShowPreview(false)
      setMappedPreview(null)
    }
  }, [isPanelOpen])

  return {
    mappedPreview,
    showPreview,
    isFetchingRecord,
    generatePreview
  }
}
